import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKLOG_VIEW_BASE, CASE_STATUS_SORT_ORDER, colorVarOf } from './shared.js';
import type { CaseRow } from './shared.js';
import { ActualCell, CaseNameButton, StatusCell } from './CaseTableCells.js';
import type { IdtCaseStatus } from '../../../lib/api.js';
import { useAuth } from '../../../hooks/index.js';

interface CaseTableProps {
  rows: CaseRow[];
  /**
   * true のとき完了 (status=完了) の行は非表示。テーブルから完了にした行は
   * スライドアウト演出を経て除外する。false (過去 Q ビュー) は渡された行をそのまま表示。
   */
  hideCompleted?: boolean;
  /**
   * この値が変わったときだけ 1 ページ目へ戻す (フィルタ / 期間 / ビューの切替キー)。
   * rows の参照変化 (ステータス編集の楽観更新による再生成など) ではページを戻さない。
   */
  resetPageKey?: string;
  /**
   * 1 ページあたりの行数。未指定時は既定の {@link DEFAULT_ROWS_PER_PAGE} (7 行)。
   * designer MyPage の担当案件一覧など、より多くの案件を一度に見せたい画面で上書きする。
   */
  rowsPerPage?: number;
}

/** 1 ページあたりの既定行数。 @graph-connects none */
const DEFAULT_ROWS_PER_PAGE = 7;

/** pmot (IDT 全員分を編集できる team)。 @graph-connects none */
const EDIT_ANY_TEAM_NAME = 'pi_pmot';

/** ステータス編集後、並べ替え (行の移動) を遅延させる時間 (ms)。 @graph-connects none */
const RESORT_DELAY_MS = 1500;

/** 完了行のスライドアウト演出の長さ (ms)。styles.css の animation と揃える。 @graph-connects none */
const EXIT_DURATION_MS = 340;

/** FLIP (行の詰め直し) のスライド時間 (ms)。 @graph-connects none */
const FLIP_DURATION_MS = 280;

/**
 * 案件明細テーブル。既定 1 ページ 7 行 (rowsPerPage で上書き可) のページネーション付き。フィルタ変更時は自動で 1 ページ目に戻る。
 * 番号→母票 / 案件名→IDpt算出シート へのリンク、担当者本人による実績・ステータスのインライン編集に対応。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI ページ下段の案件明細テーブル。子票 base で担当 designer / 種別 / 実績 / ステータスを表示・編集
 * @graph-connects useUpdateIdtCase [calls] 実績/ステータスの Backlog 書き戻し
 * @graph-connects idtKpiApi [calls] 案件名クリック時の IDpt算出シート URL 解決
 */
export function CaseTable({
  rows,
  hideCompleted = false,
  resetPageKey,
  rowsPerPage = DEFAULT_ROWS_PER_PAGE,
}: CaseTableProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentNick = user?.nickName ? user.nickName.toLowerCase() : null;
  // pmot (pi_pmot) は IDT 全員分の実績/ステータスを編集できる。
  const canEditAny = user?.teamName === EDIT_ANY_TEAM_NAME;
  const [page, setPage] = useState(0);

  // 「テーブルから完了にした行」をスライドアウト中だけ表示し続けるための集合。
  // 完了にした瞬間 StatusCell から通知され、EXIT_DURATION 後に除外する。
  const [exitingKeys, setExitingKeys] = useState<ReadonlySet<string>>(() => new Set());
  const exitTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const handleCompleting = useCallback((childIssueKey: string) => {
    if (exitTimersRef.current.has(childIssueKey)) return;
    setExitingKeys((prev) => new Set(prev).add(childIssueKey));
    const tm = setTimeout(() => {
      exitTimersRef.current.delete(childIssueKey);
      setExitingKeys((prev) => {
        const next = new Set(prev);
        next.delete(childIssueKey);
        return next;
      });
    }, EXIT_DURATION_MS);
    exitTimersRef.current.set(childIssueKey, tm);
  }, []);

  useEffect(
    () => () => {
      for (const tm of exitTimersRef.current.values()) clearTimeout(tm);
    },
    [],
  );

  // 完了行は非表示 (hideCompleted 時)。ただしスライドアウト演出中の行は表示し続ける。
  const visibleRows = useMemo(
    () =>
      hideCompleted
        ? rows.filter((r) => r.status !== '完了' || exitingKeys.has(r.childIssueKey))
        : rows,
    [rows, hideCompleted, exitingKeys],
  );

  // フィルタ / 期間 / ビューが変わったときだけ 1 ページ目へ戻す。
  // rows の参照変化 (ステータス編集の楽観更新で cases 配列が再生成される等) では
  // ページを戻さない (編集したページに留まり、退場アニメをその場で見せるため)。
  useEffect(() => {
    setPage(0);
  }, [resetPageKey]);

  // 並べ替え用ステータス。表示は row.status (楽観更新で即時) だが、ステータスを編集した行は
  // RESORT_DELAY_MS の間だけ旧ステータスで並べ、その後に並べ替える (即時にジャンプさせない)。
  // 新規行・初期表示は現在のステータスで即時に並べる。
  const sortStatusRef = useRef<Map<string, IdtCaseStatus>>(new Map());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [resortTick, setResortTick] = useState(0);

  useEffect(() => {
    const sortMap = sortStatusRef.current;
    const timers = timersRef.current;
    const liveKeys = new Set(visibleRows.map((r) => r.childIssueKey));
    for (const key of [...sortMap.keys()]) {
      if (!liveKeys.has(key)) {
        sortMap.delete(key);
        const tm = timers.get(key);
        if (tm) {
          clearTimeout(tm);
          timers.delete(key);
        }
      }
    }
    for (const r of visibleRows) {
      const cur = sortMap.get(r.childIssueKey);
      if (cur === undefined) {
        sortMap.set(r.childIssueKey, r.status); // 新規行は即時に現ステータスで並べる
      } else if (cur !== r.status && !timers.has(r.childIssueKey)) {
        const key = r.childIssueKey;
        const nextStatus = r.status;
        const tm = setTimeout(() => {
          sortStatusRef.current.set(key, nextStatus);
          timersRef.current.delete(key);
          setResortTick((n) => n + 1);
        }, RESORT_DELAY_MS);
        timers.set(key, tm);
      }
    }
  }, [visibleRows]);

  useEffect(
    () => () => {
      for (const tm of timersRef.current.values()) clearTimeout(tm);
    },
    [],
  );

  const sortedRows = useMemo(() => {
    const sortMap = sortStatusRef.current;
    return [...visibleRows].sort((a, b) => {
      const sa = CASE_STATUS_SORT_ORDER[sortMap.get(a.childIssueKey) ?? a.status];
      const sb = CASE_STATUS_SORT_ORDER[sortMap.get(b.childIssueKey) ?? b.status];
      return sa - sb || a.issueKey.localeCompare(b.issueKey);
    });
    // resortTick: 遅延タイマー発火後に再ソートさせるための依存
  }, [visibleRows, resortTick]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = sortedRows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  // FLIP: 行が詰め直される (完了行の除外・並べ替え) ときに、残った行を旧位置から
  // 新位置へ滑らせる。退場アニメ中の行は対象外 (自前の slide-out を持つため)。
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const prevTopsRef = useRef<Map<string, number>>(new Map());

  useLayoutEffect(() => {
    const newTops = new Map<string, number>();
    for (const [key, el] of rowRefs.current) {
      if (!el.isConnected) continue;
      const top = el.getBoundingClientRect().top;
      newTops.set(key, top);
      const prev = prevTopsRef.current.get(key);
      if (prev !== undefined && !exitingKeys.has(key)) {
        const delta = prev - top;
        if (Math.abs(delta) > 0.5) {
          el.style.transition = 'none';
          el.style.transform = `translateY(${String(delta)}px)`;
          requestAnimationFrame(() => {
            el.style.transition = `transform ${String(FLIP_DURATION_MS)}ms cubic-bezier(0.4,0,0.2,1)`;
            el.style.transform = '';
          });
        }
      }
    }
    prevTopsRef.current = newTops;
  });

  if (sortedRows.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('idtKpi.empty')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 min-w-0">
      <div className="overflow-x-auto rounded-md border border-[var(--color-border)]">
        <table className="w-full text-xs whitespace-nowrap">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)]">
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider w-[88px]">
                {t('idtKpi.columns.issueKey', '番号')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider">
                {t('idtKpi.columns.name', '案件名')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider w-[72px]">
                {t('idtKpi.columns.owner', '担当')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider w-[92px]">
                {t('idtKpi.columns.designerTeam', '担当team')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider w-[92px]">
                {t('idtKpi.columns.pl', 'PL')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider w-[92px]">
                {t('idtKpi.columns.mainWorker', '主担当')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider w-[112px]">
                {t('idtKpi.columns.category', '種別')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider text-right w-[72px]">
                {t('idtKpi.columns.actual', '実績')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider text-right w-[64px]">
                {t('idtKpi.columns.target', '予定')}
              </th>
              <th className="py-2 px-2.5 font-medium uppercase tracking-wider w-[112px]">
                {t('idtKpi.columns.status', '状態')}
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const ownerColorVar = colorVarOf(row.owner);
              const canEdit = canEditAny || (currentNick != null && currentNick === row.owner);
              const isExiting = exitingKeys.has(row.childIssueKey);
              return (
                <tr
                  key={row.childIssueKey}
                  ref={(el) => {
                    if (el) rowRefs.current.set(row.childIssueKey, el);
                    else rowRefs.current.delete(row.childIssueKey);
                  }}
                  className={`border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors ${
                    isExiting ? 'idt-case-row--exiting' : ''
                  }`}
                >
                  <td className="py-2 px-2.5">
                    <a
                      href={`${BACKLOG_VIEW_BASE}${row.issueKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-accent-primary)] hover:underline transition-colors"
                    >
                      {row.issueKey}
                    </a>
                  </td>
                  <td className="py-2 px-2.5 max-w-[260px]">
                    <CaseNameButton row={row} />
                  </td>
                  <td className="py-2 px-2.5">
                    <span
                      className="inline-flex justify-center px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide select-none"
                      style={{
                        background: `color-mix(in srgb, var(${ownerColorVar}) 16%, transparent)`,
                        color: `var(${ownerColorVar})`,
                      }}
                    >
                      {row.owner}
                    </span>
                  </td>
                  <td
                    className="py-2 px-2.5 text-[var(--color-text-secondary)] truncate"
                    title={row.designerTeam ?? ''}
                  >
                    {row.designerTeam ?? '—'}
                  </td>
                  <td
                    className="py-2 px-2.5 text-[var(--color-text-secondary)] truncate"
                    title={row.pl ?? ''}
                  >
                    {row.pl ?? '—'}
                  </td>
                  <td
                    className="py-2 px-2.5 text-[var(--color-text-secondary)] truncate"
                    title={row.mainWorker ?? ''}
                  >
                    {row.mainWorker ?? '—'}
                  </td>
                  <td className="py-2 px-2.5">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]">
                      {row.category}
                    </span>
                  </td>
                  <td className="py-2 px-2.5 text-right tabular-nums text-[var(--color-text-primary)]">
                    <ActualCell row={row} editable={canEdit} />
                  </td>
                  <td className="py-2 px-2.5 text-right tabular-nums text-[var(--color-text-muted)]">
                    {row.target || '—'}
                  </td>
                  <td className="py-2 px-2.5">
                    <StatusCell row={row} editable={canEdit} onCompleting={handleCompleting} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 px-1 text-[11px] text-[var(--color-text-muted)]">
        <span>
          {t('idtKpi.pagination.summary', {
            from: sortedRows.length === 0 ? 0 : safePage * rowsPerPage + 1,
            to: Math.min((safePage + 1) * rowsPerPage, sortedRows.length),
            total: sortedRows.length,
            defaultValue: '{{from}}–{{to}} / {{total}}',
          })}
        </span>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setPage((p) => Math.max(0, p - 1));
              }}
              disabled={safePage === 0}
              className="px-2 py-0.5 rounded border border-[var(--color-border)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              ‹
            </button>
            <span className="tabular-nums px-1">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => {
                setPage((p) => Math.min(totalPages - 1, p + 1));
              }}
              disabled={safePage >= totalPages - 1}
              className="px-2 py-0.5 rounded border border-[var(--color-border)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
